import {
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import ScrollToTop from "../../components/common/ScrollToTop";
import ProductCard from "../../components/frontend/products/ProductCard";
import SearchSkeleton from "../../components/frontend/search/SearchSkeleton";
import { amazonColors } from "../../config/amazonTheme";
import { generatePageMetaTags, useMetaTags } from "../../hooks/useMetaTags";
import { useSearch } from "../../hooks/useSearch";
import { Product } from "../../types/products";
import { formatApiError } from "../../utils/formatApiError";
import { trackSearch } from "../../utils/metaPixel";

// --- Helpers ---

const validateRetailer = (retailerStr: string | null): string => {
  const valid = ["amazon", "walmart"];
  return retailerStr && valid.includes(retailerStr.toLowerCase())
    ? retailerStr.toLowerCase()
    : "amazon";
};

const validatePage = (pageStr: string | null): number => {
  const page = parseInt(pageStr || "1", 10);
  return isNaN(page) || page < 1 ? 1 : page;
};

const SearchPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // --- URL Params ---
  const query = searchParams.get("query") || "";
  const rawRetailer = searchParams.get("retailer");
  const retailer = useMemo(() => validateRetailer(rawRetailer), [rawRetailer]);

  // --- Local State ---
  const [page, setPage] = useState(1);
  const [accumulatedResults, setAccumulatedResults] = useState<Product[]>([]);

  const prevQueryRef = useRef(query);
  const prevRetailerRef = useRef(retailer);

  // --- API Hook ---
  const {
    results: newResults,
    isLoading,
    error,
    refetch,
  } = useSearch({
    query,
    retailer,
    page,
  });

  useMetaTags(generatePageMetaTags.search(query));

  useEffect(() => {
    if (!query.trim()) return;

    trackSearch({
      query,
      retailer,
    });
  }, [query, retailer]);

  // --- Logic: Accumulate Results ---
  useEffect(() => {
    const isNewSearch =
      query !== prevQueryRef.current || retailer !== prevRetailerRef.current;

    if (isNewSearch) {
      setPage(1);
      setAccumulatedResults([]);
      prevQueryRef.current = query;
      prevRetailerRef.current = retailer;
      window.scrollTo({ top: 0, behavior: "auto" });
    } else if (newResults && newResults.length > 0) {
      setAccumulatedResults((prev) => {
        const existingIds = new Set(prev.map((p) => p.product_id));
        const uniqueNew = newResults.filter(
          (p) => !existingIds.has(p.product_id)
        );
        return page === 1 ? newResults : [...prev, ...uniqueNew];
      });
    }
  }, [query, retailer, newResults, page]);

  // --- Handlers ---
  const handleLoadMore = useCallback(() => {
    setPage((prev) => prev + 1);
  }, []);

  const handleTryAgain = useCallback(() => {
    setPage(1);
    setAccumulatedResults([]);
    refetch();
  }, [refetch]);

  const handleGoHome = useCallback(() => navigate("/"), [navigate]);

  // --- Derived State ---
  const isInitialLoading =
    isLoading && page === 1 && accumulatedResults.length === 0;
  const isLoadingMore = isLoading && page > 1;
  const hasResults = accumulatedResults.length > 0;

  // --- Render: Initial Loading ---
  if (isInitialLoading) {
    return (
      <Box sx={{ minHeight: "100vh" }}>
        <Container maxWidth="xl" sx={{ pt: { xs: 0, md: 0.5 }, pb: 3 }}>
          <Typography
            variant="h4"
            sx={{
              bgcolor: "grey.200",
              width: { xs: "70%", sm: 360 },
              height: 36,
              borderRadius: 1,
              mb: 2,
            }}
          />
          <SearchSkeleton />
        </Container>
      </Box>
    );
  }

  // --- Render: Error ---
  if (error && accumulatedResults.length === 0) {
    const errorMessage = formatApiError(error);

    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Container maxWidth="sm" sx={{ textAlign: "center" }}>
          <Paper
            elevation={0}
            sx={{
              p: 5,
              borderRadius: 4,
              border: "1px dashed #ccc",
              bgcolor: "white",
            }}
          >
            <Typography variant="h2" sx={{ mb: 2 }}>
              😕
            </Typography>
            <Typography
              variant="h5"
              fontWeight="bold"
              gutterBottom
            >
              {errorMessage}
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                variant="contained"
                onClick={handleTryAgain}
                sx={{ bgcolor: "black" }}
                className="notranslate"
                translate="no"
              >
                {t("search.tryAgain")}
              </Button>
              <Button
                variant="outlined"
                onClick={handleGoHome}
                className="notranslate"
                translate="no"
              >
                {t("search.goHome")}
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>
    );
  }

  // --- Render: Main Content ---
  return (
    <Box sx={{ minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ pt: { xs: 0, md: 0.5 } }}>
        {/* Search Header */}
        <Box
          sx={{ mt: { xs: 1, md: 1.5 }, mb: { xs: 2.5, md: 3.5 } }}
          className="notranslate"
          translate="no"
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 800,
              fontSize: { xs: "1.5rem", md: "2rem" },
              color: "#1a1a1a",
            }}
          >
            {hasResults
              ? t("search.showingResultsFor", { query })
              : t("search.noResults")}
          </Typography>
        </Box>

        {/* No Results State */}
        {!hasResults && !isLoading && (
          <Box sx={{ textAlign: "center", mt: 8, color: "text.secondary" }}>
            <Typography variant="h6" className="notranslate" translate="no">
              {t("search.noResultsDesc")}
            </Typography>
            <Button
              variant="outlined"
              sx={{ mt: 2 }}
              onClick={handleGoHome}
              className="notranslate"
              translate="no"
            >
              {t("search.goHome")}
            </Button>
          </Box>
        )}

        {/* Product Grid - Strict 6 Columns on Large Screens */}
        <Box
          sx={{
            display: "grid",
            gap: { xs: 1.5, sm: 1.5 },
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(3, 1fr)",
              md: "repeat(4, 1fr)",
              lg: "repeat(5, 1fr)",
              xl: "repeat(6, 1fr)",
            },
          }}
        >
          {accumulatedResults.map((product, index) => (
            <ProductCard
              key={`${product.product_id}-${index}`}
              product={product}
              retailer={retailer}
              index={index}
            />
          ))}
        </Box>

        {/* Load More Button */}
        {hasResults && (
          <Box sx={{ textAlign: "center", mt: 6 }}>
            {isLoadingMore ? (
              <CircularProgress size={30} sx={{ color: "black" }} />
            ) : (
              newResults.length > 0 && (
                <Button
                  variant="contained"
                  onClick={handleLoadMore}
                  size="large"
                  className="notranslate"
                  translate="no"
                  sx={{
                    px: 6,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 700,
                    bgcolor: amazonColors.orange,
                    color: "white",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    "&:hover": {
                      bgcolor: amazonColors.orangeHover,
                      boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                    },
                  }}
                >
                  {t("search.loadMore")}
                </Button>
              )
            )}
          </Box>
        )}

        <ScrollToTop threshold={400} />
      </Container>
    </Box>
  );
};

export default SearchPage;
